import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Image,
  SafeAreaView,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { NavigationProp, RouteProp } from '@react-navigation/native';
import OrnamentBackground from '../../components/OrnamentBackground';
import VerificationCodeInput from '../../components/VerificationCodeInput';
import { API_URL } from '../../config';

import { createLogger } from '../../utils/logger';
const logger = createLogger('PasswordVerificationCode');

type RouteParams = {
  identifier: string;
  username?: string;
  type: 'email' | 'phone';
};

const PasswordVerificationCodeScreen = ({
  navigation,
  route,
}: {
  navigation: NavigationProp<any>;
  route: RouteProp<any, any>;
}) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const params = route.params as RouteParams | undefined;
  const identifier = params?.identifier ?? 'test@example.com'; // Fallback for testing
  const type = params?.type ?? 'email'; // Fallback for testing

  // Separate timers for code expiry (15 min) and resend cooldown (60 sec)
  const [codeExpiryTime, setCodeExpiryTime] = useState(900); // 15 minutes for code expiry
  const [resendCooldown, setResendCooldown] = useState(60); // 60 seconds before resend is enabled

  useEffect(() => {
    const timer = setInterval(() => {
      setCodeExpiryTime((prevTime) => {
        if (prevTime <= 0) return 0;
        return prevTime - 1;
      });
      setResendCooldown((prevTime) => {
        if (prevTime <= 0) return 0;
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleResendCode = async () => {
    if (isResending) return;

    setIsResending(true);
    setError(false);
    setErrorMessage('');

    try {
      const response = await fetch(`${API_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: identifier }),
      });

      // Reset timers and code on success
      setCodeExpiryTime(900);
      setResendCooldown(60);
      setCode('');
    } catch (error) {
      logger.error('Resend code error:', error);
    } finally {
      setIsResending(false);
    }
  };

  const handleVerify = async () => {
    if (isLoading) return;

    setIsLoading(true);
    setError(false);
    setErrorMessage('');

    try {
      const response = await fetch(`${API_URL}/auth/verify-reset-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: identifier,
          code: code
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        navigation.navigate('ResetPassword', {
          identifier,
          verificationCode: code
        });
      } else {
        setError(true);
        setErrorMessage(result.message || 'Invalid verification code');
      }
    } catch (error) {
      logger.error('Verify code error:', error);
      setError(true);
      setErrorMessage('Failed to verify code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseError = () => {
    setError(false);
    setCode(''); // Reset the code input
  };

  return (
    <SafeAreaView style={styles.container}>
      <OrnamentBackground />

      <View style={styles.topSection}>
        <Image source={require('../../assets/logo.png')} style={styles.logo} />
        <Text style={styles.welcomeText}>Verify Your Account</Text>
        <Text style={styles.description}>
          We've sent a verification code to your {type === 'email' ? 'email' : 'phone'}{' '}
          {identifier}. Please enter it below to continue with password reset.
        </Text>
      </View>

      <View style={styles.mainContainer}>
        {error && (
          <View style={styles.errorContainer}>
            <View style={styles.errorContent}>
              <Text style={styles.errorTitle}>Verification Code Invalid</Text>
              <Text style={styles.errorMessage}>
                {errorMessage || "Seems like you've entered the wrong code, please re-check and input it again."}
              </Text>
              <TouchableOpacity style={styles.closeButton} onPress={handleCloseError}>
                <Text style={styles.closeButtonText}>×</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        <View style={styles.inputContainer}>
          <ScrollView style={styles.scrollView}>
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Verification Code</Text>
              <VerificationCodeInput value={code} onChangeText={setCode} />
              <Text style={styles.timerText}>
                Code expires in: {formatTime(codeExpiryTime)}
              </Text>
            </View>

            <View style={styles.buttonContainer}>
              <TouchableOpacity
                style={[styles.verifyButton, { opacity: code.length === 6 && !isLoading ? 1 : 0.6 }]}
                onPress={handleVerify}
                disabled={code.length !== 6 || isLoading}
              >
                {isLoading ? (
                  <ActivityIndicator color="#FFFFFF" />
                ) : (
                  <Text style={styles.verifyButtonText}>Verify</Text>
                )}
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.resendContainer}
                onPress={handleResendCode}
                disabled={resendCooldown > 0 || isResending}
              >
                {isResending ? (
                  <ActivityIndicator color="#29BFC0" size="small" />
                ) : (
                  <Text style={[
                    styles.resendText,
                    { opacity: resendCooldown > 0 ? 0.5 : 1 }
                  ]}>
                    {resendCooldown > 0
                      ? `Resend code in ${resendCooldown}s`
                      : "Didn't receive the code? Resend"}
                  </Text>
                )}
              </TouchableOpacity>
            </View>

            <View style={styles.bottomPadding} />
          </ScrollView>
        </View>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
    flex: 0.68,
  },
  logo: {
    width: s(120),
    height: s(120),
    marginBottom: ms(16),
  },
  welcomeText: {
    fontSize: ms(28, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
  },
  description: {
    fontSize: ms(16, 0.3),
    color: '#FFFFFF',
    textAlign: 'center',
    marginBottom: ms(20),
    paddingHorizontal: ms(40),
  },
  mainContainer: {
    flex: 0.98,
    position: 'relative',
  },
  errorContainer: {
    backgroundColor: '#E85C5C',
    paddingVertical: ms(25),
    paddingHorizontal: ms(20),
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    marginBottom: ms(-20),
    position: 'relative',
    zIndex: 1,
  },
  errorContent: {
    flexDirection: 'column',
    alignItems: 'center',
  },
  errorTitle: {
    fontSize: ms(16, 0.3),
    color: '#FFFFFF',
    fontWeight: 'bold',
    marginBottom: ms(10),
  },
  errorMessage: {
    fontSize: ms(14, 0.3),
    color: '#FFFFFF',
    textAlign: 'center',
    opacity: 0.8,
  },
  closeButton: {
    position: 'absolute',
    right: 0,
    top: 0,
    padding: ms(5),
  },
  closeButtonText: {
    color: '#FFFFFF',
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(30),
    position: 'relative',
    zIndex: 2,
  },
  scrollView: {
    paddingHorizontal: ms(16),
  },
  inputGroup: {
    marginBottom: ms(5),
    alignItems: 'center',
  },
  label: {
    fontSize: ms(22, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: ms(20),
    textAlign: 'center',
  },
  timerText: {
    fontSize: ms(14, 0.3),
    color: '#6B7280',
    marginTop: ms(5),
  },
  buttonContainer: {
    paddingVertical: ms(20),
    paddingHorizontal: ms(16),
  },
  verifyButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  verifyButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  resendContainer: {
    alignItems: 'center',
    marginTop: ms(16),
  },
  resendText: {
    color: '#29BFC0',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  bottomPadding: {
    height: s(30),
  },
});

export default PasswordVerificationCodeScreen;